import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LogIn, UserPlus, Sparkles } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n';
import AuthModal from '@/components/AuthModal';

const DISMISS_KEY = 'welcome_popup_dismissed_at';
const DISMISS_WINDOW_MS = 24 * 60 * 60 * 1000; // 24h
const SHOW_DELAY_MS = 7000; // 7s after first render

function wasRecentlyDismissed() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ts = Number(v);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_WINDOW_MS;
  } catch (_e) { return false; }
}

function markDismissed() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_e) { /* ignore */ }
}

export default function WelcomeDialog() {
  const { user } = useAuth();
  const { t } = useI18n();
  const isAnon = !user || user === false;
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState('register');

  useEffect(() => {
    if (!isAnon) return undefined;
    if (wasRecentlyDismissed()) return undefined;
    const timer = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isAnon]);

  // If the user signs in/up while open, close the dialog automatically
  useEffect(() => {
    if (!isAnon && open) { setOpen(false); setAuthOpen(false); }
  }, [isAnon, open]);

  const close = (persist = true) => {
    if (persist) markDismissed();
    setOpen(false);
  };

  const openAuth = (tab) => {
    setAuthTab(tab);
    setOpen(false);
    setAuthOpen(true);
    // Don't persist dismissal so the popup can return tomorrow if they bail out
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) close(true); }}>
        <DialogContent
          data-testid="welcome-dialog"
          className="sm:max-w-md bg-[#0A0A0A] border-[#FF007F]/30 text-white"
        >
          <DialogHeader>
            <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF007F] to-[#00F0FF] grid place-items-center mb-2">
              <Sparkles className="w-6 h-6 text-black" />
            </div>
            <DialogTitle className="text-center font-display text-2xl font-black uppercase tracking-tighter">
              {t('welcome.title') || 'Bienvenue sur Horizon.Meet&Cruise'}
            </DialogTitle>
            <DialogDescription className="text-center text-white/60 text-sm">
              {t('welcome.subtitle') || "Rejoins la communauté pour marquer tes spots, lancer des car meets et rouler en convoi avec d'autres pilotes FH6."}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 grid gap-2">
            <Button
              data-testid="welcome-register-btn"
              onClick={() => openAuth('register')}
              className="w-full h-12 rounded-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {t('welcome.register') || 'Créer un compte'}
            </Button>
            <Button
              data-testid="welcome-login-btn"
              onClick={() => openAuth('login')}
              variant="outline"
              className="w-full h-12 rounded-full border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 hover:text-[#00F0FF] font-bold uppercase tracking-wide"
            >
              <LogIn className="w-4 h-4 mr-2" />
              {t('welcome.login') || "J'ai déjà un compte"}
            </Button>
            <button
              type="button"
              onClick={() => close(true)}
              className="mt-2 text-xs text-white/40 hover:text-white/70 underline-offset-2 hover:underline"
              data-testid="welcome-later-btn"
            >
              {t('welcome.later') || 'Plus tard'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultTab={authTab} />
    </>
  );
}
